if (!customElements.get('ppp-planner-features')) {
  class PppPlannerFeatures extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.tabs = Array.from(this.querySelectorAll('[data-planner-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-planner-panel]'));
      this.mobileLabels = Array.from(this.querySelectorAll('[data-planner-mobile-label]'));
      this.previousButton = this.querySelector('[data-planner-previous]');
      this.nextButton = this.querySelector('[data-planner-next]');
      this.abortController = new AbortController();

      if (!this.tabs.length || !this.panels.length) return;

      const options = { signal: this.abortController.signal };
      this.addEventListener('click', this.handleClick.bind(this), options);
      this.addEventListener('keydown', this.handleKeydown.bind(this), options);
      document.addEventListener('shopify:block:select', this.handleBlockSelect.bind(this), options);

      const selectedIndex = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      this.setActive(selectedIndex < 0 ? 0 : selectedIndex, false);
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
      this.initialized = false;
    }

    handleClick(event) {
      const tab = event.target.closest('[data-planner-tab]');
      if (tab && this.contains(tab)) {
        this.setActive(Number(tab.dataset.plannerIndex), true);
        return;
      }

      if (event.target.closest('[data-planner-previous]')) {
        this.setActive(this.activeIndex - 1, true);
        return;
      }

      if (event.target.closest('[data-planner-next]')) {
        this.setActive(this.activeIndex + 1, true);
      }
    }

    handleKeydown(event) {
      const tab = event.target.closest('[data-planner-tab]');
      if (!tab || !this.contains(tab)) return;

      const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
      if (!keys.includes(event.key)) return;

      event.preventDefault();
      const currentIndex = Number(tab.dataset.plannerIndex);
      let nextIndex = currentIndex;

      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.tabs.length - 1;

      this.setActive(nextIndex, true);
      this.tabs[nextIndex].focus();
    }

    handleBlockSelect(event) {
      if (!event.detail || event.detail.sectionId !== this.dataset.sectionId) return;

      const selectedTab = this.tabs.find((tab) => tab.dataset.blockId === event.detail.blockId);
      if (!selectedTab) return;

      this.setActive(Number(selectedTab.dataset.plannerIndex), false);
    }

    setActive(index, animate) {
      const nextIndex = Math.max(0, Math.min(index, this.tabs.length - 1));
      const update = () => {
        const activeElement = document.activeElement;
        this.activeIndex = nextIndex;

        this.tabs.forEach((tab, tabIndex) => {
          const isActive = tabIndex === nextIndex;
          tab.classList.toggle('is-active', isActive);
          tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
          tab.setAttribute('tabindex', isActive ? '0' : '-1');
        });

        this.panels.forEach((panel, panelIndex) => {
          panel.toggleAttribute('hidden', panelIndex !== nextIndex);
        });

        this.mobileLabels.forEach((label, labelIndex) => {
          label.toggleAttribute('hidden', labelIndex !== nextIndex);
        });

        if (this.previousButton) {
          this.previousButton.toggleAttribute('hidden', nextIndex === 0);
          if (nextIndex > 0) {
            this.previousButton.setAttribute('aria-label', this.tabs[nextIndex - 1].textContent.trim());
          }
        }

        if (this.nextButton) {
          this.nextButton.toggleAttribute('hidden', nextIndex === this.tabs.length - 1);
          if (nextIndex < this.tabs.length - 1) {
            this.nextButton.setAttribute('aria-label', this.tabs[nextIndex + 1].textContent.trim());
          }
        }

        if (activeElement === this.previousButton && nextIndex === 0 && this.nextButton) {
          this.nextButton.focus();
        }

        if (activeElement === this.nextButton && nextIndex === this.tabs.length - 1 && this.previousButton) {
          this.previousButton.focus();
        }
      };

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (animate && document.startViewTransition && !reduceMotion) {
        document.startViewTransition(update);
      } else {
        update();
      }
    }
  }

  customElements.define('ppp-planner-features', PppPlannerFeatures);
}
